import axios, { AxiosInstance } from 'axios'
import { CoreConfigurationDto } from '@tedsinc/teds-server/dist/src/resources/v1/core/configuration/coreConfigurationSchema'
import TedsLogo from '@/components/Logos/TedsLogo/TedsLogo.vue'
import tedsUi from '@tedsinc/teds-components'
import { VueConstructor } from 'vue/types/umd'
import { TedsUI } from '@tedsinc/teds-components/types/src/tedsUI/tedsUI'
import { AuthProvider } from '@tedsinc/teds-components/types/src/tedsUI/auth/providers/authProvider'
import { adminUser, resolveFullName, whoAmI, whoAmITesting } from './app/user/whoAmI'
import { User } from './app/definitions/User'
import { HttpSettings } from './app/httpSettings/HttpSettings'
import Ajv from 'ajv'
import { httpSettingsJsonSchema } from './app/httpSettings/httpSettingsJsonSchema'
import {
  TEDS_DEFAULT_BACKGROUND_FONT_COLOR,
  TEDS_DEFAULT_LOGO_BASE_64,
  TEDS_DEFAULT_PRIMARY_COLOR,
} from './tedsConfigurationDefaults'
import { LauncherServerSettings } from './app/definitions/LauncherServerSettings'
import { testCoreConfiguration, getCoreConfiguration } from './app/configuration/configuration'
import { standardizeHexColorCasing } from './app/uiSettings/uiSettings'

export type TedsUIWithFeatureFlags = TedsUI & {
  options: { featureFlags: CoreConfigurationDto['featuresEnabledForUser'] }
}
export type TedsUIOptionsWithFeatureFlags = TedsUIWithFeatureFlags['options']

/**
 * Generator for default teds ui options.
 * @returns
 */
function createDefaultTedsUiOptions(isDev = false): TedsUIOptionsWithFeatureFlags {
  return {
    applicationName: 'Teds Launcher',
    applicationLogo: TedsLogo,
    clientLogo: TEDS_DEFAULT_LOGO_BASE_64,
    primaryColor: standardizeHexColorCasing(TEDS_DEFAULT_PRIMARY_COLOR),
    backgroundFontColor: standardizeHexColorCasing(TEDS_DEFAULT_BACKGROUND_FONT_COLOR),
    useProfileImages: true,
    menuColor: standardizeHexColorCasing('#333333'),
    menuTextColor: standardizeHexColorCasing('#ffffff'),
    logLevel: 'trace',
    useLogger: true,
    user: {
      name: 'Unknown',
      initials: 'NA',
    },
    isDev,
    featureFlags: testCoreConfiguration.featuresEnabledForUser,
  }
}

/**
 * Definition of the bootstate
 */
export interface BootState {
  user: User
  tedsUi: TedsUIWithFeatureFlags
  httpSettings: HttpSettings
  request: AxiosInstance
  auth?: AuthProvider<any> //eslint-disable-line
}

/**
 * Display user message before the application is loaded
 * @param message
 * @param backgroundColor
 * @param fontColor
 */
export function userMessage(message: string, backgroundColor: string = '#B11A1A', fontColor: string = 'white'): void {
  const element = document.createElement('div')
  element.innerHTML = message
  element.style.position = 'absolute'
  element.style.transform = 'translate(-50%, -50%)'
  element.style.width = '50%'
  element.style.padding = '16px'
  element.style.borderRadius = '8px'
  element.style.background = backgroundColor
  element.style.color = fontColor
  element.style.top = '50%'
  element.style.left = '50%'
  element.style.textAlign = 'center'
  document.body.appendChild(element)
  const app = document.querySelector('#app')
  if (app) {
    app.innerHTML = ''
    app.appendChild(element)
  } else {
    throw new Error('#app element does not exist')
  }
}

/**
 * Validate http settings using its json schema
 * @param httpSettings
 * @returns
 */
export function validateHttpSettingsAndReturn(httpSettings: HttpSettings): HttpSettings {
  const validator = new Ajv().compile(httpSettingsJsonSchema)
  validator(httpSettings)
  if (validator.errors) {
    console.error('http-setting.json does not meet the spec.')
    throw new Error(validator.errors.map(error => error.message).join('\n'))
  }
  return httpSettings
}

/**
 * Gets the http settings from the local install
 * @returns
 */
export async function getHttpSettings(): Promise<HttpSettings> {
  try {
    const response = await axios.get<HttpSettings>('./http-settings.json')
    return validateHttpSettingsAndReturn(response.data)
  } catch (err) {
    console.error(err)
    userMessage(`Could not load application Settings. Please notify your administrator.`)
    throw new Error(`Could not load ./http-setting.json`)
  }
}
/**
 * Enforces the user is logged in
 * @param ui
 * @param httpSettings
 * @returns
 */
async function enforceLogin(
  ui: TedsUI,
  httpSettings: HttpSettings
): Promise<AuthProvider<any>> /* eslint-disable-line */ {
  const keycloak = httpSettings.auth.providers.keycloak
  ui.log.info(`starting authentication process`)
  const timeout1 = setTimeout(
    () => userMessage('Connecting to your authentication provider. Please hold.', 'deepskyblue', 'white'),
    1500
  )
  const timeout2 = setTimeout(
    () => userMessage('Unable to connect to your authentication provider. Please notify your administrator.'),
    httpSettings.auth.globalAuthSettings.authenticationTimeoutMs
  )
  const provider = await ui.getKeycloakAuthProvider(keycloak.version, keycloak.javascriptClientAssets)
  const auth = await provider.init(true, keycloak.configuration)
  auth.startRefreshingToken(keycloak.minValidityMs / 1000, keycloak.refreshRateMs)
  if (httpSettings.auth.globalAuthSettings.logActivity) {
    auth.events.on('remaining.seconds', seconds => console.debug(`[keycloak] remaining seconds left on token`, seconds))
    auth.events.on('token.refresh.success', () => console.debug(`[keycloak] token refreshed`, auth.token()))
  }
  ui.log.info(`authentication completed`)
  clearTimeout(timeout1)
  clearTimeout(timeout2)
  return auth
}

/**
 * Updates the application settings after authorization
 * @param request
 * @param ui
 */
export async function updateApplicationSettings(
  request: AxiosInstance,
  ui: TedsUI,
  user: User,
  httpSettings: HttpSettings
): Promise<TedsUI> {
  try {
    const overrides = httpSettings.app.serverSettingsOverride
    const [userNameFirstInitial, userNameSecondInitial] = resolveFullName(user)
      .split(' ')
      .map(name => name.charAt(0))
    const response = await request.get<LauncherServerSettings>('/api/v1/core/settings/launcher')
    ui.options.backgroundFontColor = standardizeHexColorCasing(
      overrides.fontColor || response.data.ui_background_font_color || TEDS_DEFAULT_BACKGROUND_FONT_COLOR
    )
    ui.options.primaryColor = standardizeHexColorCasing(
      overrides.primaryColor || response.data.ui_primary_color || TEDS_DEFAULT_PRIMARY_COLOR
    )
    ui.options.clientLogo = overrides.clientLogo || response.data.ui_logo_base64 || TEDS_DEFAULT_LOGO_BASE_64
    ui.options.user = {
      initials: `${userNameFirstInitial}${userNameSecondInitial}`,
      name: resolveFullName(user),
    }
    return ui
  } catch (err) {
    console.error(err)
    userMessage('Could not load application Settings. Please notify your administrator.')
    throw err
  }
}

/**
 * Create an html element to let the viewer of the application know
 * they are running in dev mode
 */
export function insertDevElement(): void {
  const element = document.createElement('div')
  element.innerHTML = 'DEV MODE ENABLED'
  element.style.position = 'fixed'
  element.style.background = 'black'
  element.style.padding = '2px 8px'
  element.style.fontSize = '16px'
  element.style.color = 'white'
  element.style.top = '0'
  element.style.left = '16px'
  element.style.zIndex = '100000'
  document.body.appendChild(element)
}

const attachFeatureFlagsToTedsUI = (ui: TedsUI, configuration: CoreConfigurationDto): TedsUIWithFeatureFlags => {
  const fullOptions: BootState['tedsUi']['options'] = Object.assign({}, ui.options, {
    featureFlags: configuration.featuresEnabledForUser,
  })
  ui.options = fullOptions
  const hasFeatureFlags = (ui: TedsUI): ui is BootState['tedsUi'] => ui.options.hasOwnProperty('featureFlags')
  if (!hasFeatureFlags(ui)) {
    throw new Error('Did not load feature flags correctly')
  }
  return ui
}

/**
 * Created to solve an order of operations problem
 * @param Vue
 * @returns
 */
export async function bootControllerTest(
  Vue: VueConstructor,
  asRole: User,
  settings?: HttpSettings,
  testMode?: boolean,
  featureFlags: CoreConfigurationDto = testCoreConfiguration
): Promise<BootState> {
  const httpSettings = settings ? settings : await getHttpSettings()
  const { user, request } = testMode ? whoAmITesting(httpSettings, asRole) : await whoAmI(httpSettings)
  const configuration = testMode ? featureFlags : await getCoreConfiguration(httpSettings)
  const ui = await tedsUi.createTedsUI(Vue, createDefaultTedsUiOptions(testMode))
  const uiWithFeatureFlags = attachFeatureFlagsToTedsUI(ui, configuration)
  if (httpSettings.auth.globalAuthSettings.useAuthentication) {
    await enforceLogin(uiWithFeatureFlags, httpSettings)
  }
  if (!testMode) {
    await updateApplicationSettings(request, uiWithFeatureFlags, user, httpSettings)
  }
  return {
    httpSettings,
    tedsUi: uiWithFeatureFlags,
    request,
    user,
  }
}

/**
 * Created to solve an order of operations problem
 *
 * - This function is ignored in coverage because of keycloak and unit testing
 * @param Vue
 * @returns
 */
/* istanbul ignore next */
export async function bootController(Vue: VueConstructor): Promise<BootState> {
  const httpSettings = await getHttpSettings()
  const options = createDefaultTedsUiOptions()
  options.logLevel = httpSettings.logging.level
  if (httpSettings.services.globalServiceSettings.useMockServices) {
    insertDevElement()
    options.isDev = true
    return bootControllerTest(Vue, adminUser, httpSettings, false)
  }
  const ui = await tedsUi.createTedsUI(Vue, options)
  const auth = httpSettings.auth.globalAuthSettings.useAuthentication ? await enforceLogin(ui, httpSettings) : undefined
  const { user, request } = await whoAmI(httpSettings, auth)
  const configuration = await getCoreConfiguration(httpSettings, auth)
  const uiWithFeatureFlags = attachFeatureFlagsToTedsUI(ui, configuration)
  await updateApplicationSettings(request, uiWithFeatureFlags, user, httpSettings)
  return {
    httpSettings,
    tedsUi: uiWithFeatureFlags,
    request,
    auth,
    user,
  }
}
